import React, { useState, useEffect, useRef } from "react";
import Button from "../../components/ui/buttons/Button";
import { updateSMSNumber, updateSMSPermission } from "../../services/api/core";
import { UI_COPY } from "../../config";
import { useUiText } from "../../hooks/useUiText";
import {
  formatSMSPhoneNumberInput,
  validateSMSPhoneNumber,
} from "../../utils/auth";

const HEIGHT_TRANSITION_MS = 300;
const FADE_MS = 250;

/**
 * SMS opt-in form (phone + permission) for the new-user modal.
 * Shown only when the user's SMS permission is still Not Set.
 * After save, fades the form into a green confirmation box.
 *
 * @param {boolean} isOpen Whether the parent modal is open; resets local state.
 * @param {Function} onSaved Called after accept or reject succeeds (modal stays open).
 * @param {string} [initialPhoneNumber=""] Pre-filled value from `user.sms_number`.
 * @returns {JSX.Element} SMS permission form, or the success confirmation.
 */
const SMSPermissionSection = ({
  isOpen,
  onSaved,
  initialPhoneNumber = "",
}) => {
  const { t } = useUiText();
  const [phoneNumber, setPhoneNumber] = useState(initialPhoneNumber);
  const [confirmChecked, setConfirmChecked] = useState(true);
  const [phoneError, setPhoneError] = useState("");
  const [permissionError, setPermissionError] = useState("");
  const [saveError, setSaveError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [view, setView] = useState("form");
  const [isFading, setIsFading] = useState(false);
  const wrapperRef = useRef(null);
  const onSavedRef = useRef(onSaved);
  onSavedRef.current = onSaved;

  /**
   * Format the phone input and toggle the permission checkbox when the
   * number is valid.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} e Input change event.
   * @returns {void}
   */
  const handlePhoneChange = (e) => {
    const digits = formatSMSPhoneNumberInput(e.target.value, phoneNumber);

    setPhoneNumber(digits);

    if (validateSMSPhoneNumber(digits)) {
      setPhoneError("");
      setConfirmChecked(true);
    } else {
      setPhoneError(t(UI_COPY.sms.numberFormat));
      setConfirmChecked(false);
    }
  };

  /**
   * Update the SMS permission checkbox and clear the permission error when
   * the box is checked.
   *
   * @param {React.ChangeEvent<HTMLInputElement>} e Checkbox change event.
   * @returns {void}
   */
  const handlePermissionChange = (e) => {
    setConfirmChecked(e.target.checked);
    if (e.target.checked) {
      setPermissionError("");
    }
  };

  /**
   * Save the phone number and set SMS permission to accepted, then
   * reveal the success confirmation.
   *
   * @returns {Promise<void>}
   */
  const handleAccept = async () => {
    if (isSubmitting) {
      return;
    }

    const isPhoneValid = validateSMSPhoneNumber(phoneNumber);

    if (!isPhoneValid) {
      setPhoneError(t(UI_COPY.sms.numberFormat));
      return;
    }

    if (!confirmChecked) {
      setPermissionError(t(UI_COPY.sms.permissionRequired));
      return;
    }

    setIsSubmitting(true);
    setSaveError("");

    try {
      await updateSMSNumber(phoneNumber);
      await updateSMSPermission("accepted");
      revealSuccess();
    } catch (error) {
      console.error("Error updating SMS permissions:", error);
      setSaveError(t(UI_COPY.sms.saveError));
      setIsSubmitting(false);
    }
  };

  /**
   * Set SMS permission to rejected (without requiring a valid phone) and
   * reveal the success confirmation.
   *
   * @returns {Promise<void>}
   */
  const handleReject = async () => {
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);
    setSaveError("");

    try {
      await updateSMSPermission("rejected");
      revealSuccess();
    } catch (error) {
      console.error("Error rejecting SMS permissions:", error);
      setSaveError(t(UI_COPY.sms.saveError));
      setIsSubmitting(false);
    }
  };

  /**
   * Lock the current height, fade the form out, then swap to success.
   *
   * @returns {void}
   */
  const revealSuccess = () => {
    const wrapper = wrapperRef.current;
    if (wrapper) {
      wrapper.style.transition = "none";
      wrapper.style.height = `${wrapper.offsetHeight}px`;
    }
    setIsFading(true);
    window.setTimeout(() => {
      setView("success");
      setIsSubmitting(false);
    }, FADE_MS);
  };

  /**
   * After success mounts, ease the locked height down to the confirmation box.
   */
  useEffect(() => {
    if (view !== "success") {
      return undefined;
    }

    const wrapper = wrapperRef.current;
    if (!wrapper) {
      onSavedRef.current();
      return undefined;
    }

    const endHeight = wrapper.firstElementChild?.offsetHeight ?? 0;
    let savedTimer = 0;

    const frameId = window.requestAnimationFrame(() => {
      wrapper.style.transition = `height ${HEIGHT_TRANSITION_MS}ms ease-out`;
      setIsFading(false);
      window.requestAnimationFrame(() => {
        wrapper.style.height = `${endHeight}px`;
      });
      savedTimer = window.setTimeout(() => {
        wrapper.style.height = "auto";
        onSavedRef.current();
      }, HEIGHT_TRANSITION_MS);
    });

    return () => {
      window.cancelAnimationFrame(frameId);
      window.clearTimeout(savedTimer);
    };
  }, [view]);

  useEffect(() => {
    if (isOpen) {
      setPhoneNumber(initialPhoneNumber);
      setConfirmChecked(true);
      setPhoneError("");
      setPermissionError("");
      setSaveError("");
      setView("form");
      setIsFading(false);
      setIsSubmitting(false);
      if (wrapperRef.current) {
        wrapperRef.current.style.height = "auto";
        wrapperRef.current.style.transition = "none";
      }
    }
  }, [isOpen, initialPhoneNumber]);

  const successBox = (
    <div className="rounded-lg border border-green-200 bg-green-100 px-4 py-6 text-center">
      <p className="text-base font-semibold text-green-800">
        {t(UI_COPY.sms.successSet)}
      </p>
    </div>
  );

  return (
    <div ref={wrapperRef} className="overflow-hidden">
    <div
      className={`flex flex-col text-center transition-all duration-300 ease-out ${
        isFading ? "-translate-y-1 opacity-0" : "translate-y-0 opacity-100"
      }`}
    >
      {view === "success" ? (
        successBox
      ) : (
        <>
      <p className="text-sm text-gray-600">
        <i>{t(UI_COPY.sms.cellPhoneNumber)}</i>
      </p>

      <div className="relative">
        <input
          type="tel"
          className={`min-h-[52px] w-full rounded-md border p-3 text-center text-xl ${
            phoneError ? "border-red-500" : "border-gray-300"
          }`}
          pattern="[0-9]{3}-[0-9]{3}-[0-9]{4}"
          placeholder="123-456-7890"
          value={phoneNumber}
          onChange={handlePhoneChange}
          disabled={isSubmitting}
        />
        {phoneNumber && (
          <button
            type="button"
            onClick={() => {
              setPhoneNumber("");
              setPhoneError(t(UI_COPY.sms.numberFormat));
            }}
            className="absolute right-3 top-1/2 min-h-[44px] min-w-[44px] -translate-y-1/2 text-gray-400 hover:text-gray-600"
            disabled={isSubmitting}
          >
            ✕
          </button>
        )}
      </div>

      <p
        className={`mt-2 text-sm ${phoneError ? "text-red-600" : "text-gray-600"}`}
      >
        <i>{phoneError || t(UI_COPY.sms.numberFormat)}</i>
      </p>

      <div className="mt-2 rounded-md bg-gray-100 p-4">
        <div
          className={`flex items-center gap-3 rounded-md p-3 ${confirmChecked ? "bg-green-100" : "bg-gray-200"}`}
        >
          <input
            type="checkbox"
            id="smsPermission"
            className="h-6 w-6 shrink-0"
            checked={confirmChecked}
            onChange={handlePermissionChange}
            disabled={isSubmitting}
          />
          <label htmlFor="smsPermission" className="flex-1 text-left text-sm">
            {t(UI_COPY.sms.permissionLabel)}
          </label>
        </div>

        <p className="mt-3 text-left text-xs text-gray-600">
          {t(UI_COPY.sms.permissionHelp)}{" "}
          <a
            href="/app/sms-terms"
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-900 hover:underline"
          >
            {t(UI_COPY.sms.termsLink)}
          </a>
        </p>
      </div>

      {permissionError && (
        <p className="mt-2 text-sm text-red-600">{permissionError}</p>
      )}
      {saveError ? (
        <p className="mt-2 text-sm text-red-600" role="alert">
          {saveError}
        </p>
      ) : null}

      <div className="mt-2 flex flex-col gap-3 sm:flex-row">
        <Button
          onClick={handleReject}
          text={isSubmitting ? t(UI_COPY.sms.processing) : t(UI_COPY.sms.reject)}
          variant={isSubmitting || confirmChecked ? "disabled" : "secondary"}
          disabled={isSubmitting || confirmChecked}
          className="min-h-[52px] w-full text-base"
        />
        <Button
          onClick={handleAccept}
          text={isSubmitting ? t(UI_COPY.sms.processing) : t(UI_COPY.sms.confirm)}
          variant={isSubmitting || !confirmChecked ? "disabled" : "primary"}
          disabled={isSubmitting || !confirmChecked}
          className="min-h-[52px] w-full text-base"
        />
      </div>
        </>
      )}
    </div>
    </div>
  );
};

export default SMSPermissionSection;
